import { useEffect, useState, useCallback } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { PaymentBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { Wallet, TrendingUp, Coins, Receipt, HandCoins, Printer, X, CalendarDays, Download, ArrowUpDown, Plus, Save } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { motion } from "framer-motion";
import { toast } from "sonner";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return `${MONTHS[Number(mo) - 1]} ${y}`;
};
const idrShort = (n) => "Rp " + new Intl.NumberFormat("id-ID").format(Math.round(Number(n || 0)));

const VAR_COSTS = [
  ["gaji_karyawan", "Gaji Karyawan"], ["gaji_freelance", "Gaji Freelance"], ["biaya_iklan", "Biaya Iklan"], ["box", "Box"],
  ["kertas_hvs", "Kertas HVS"], ["kertas_foto", "Kertas Foto"], ["pigura", "Pigura"],
  ["boneka", "Boneka"], ["goodie_bag", "Goodie Bag"], ["tinta_printer", "Tinta Printer"], ["printer", "Printer"],
  ["risalah", "Risalah"], ["biaya_lain", "Biaya Lain-lain"],
];
const FIX_COSTS = [["penyusutan_kantor", "Penyusutan Kantor"], ["cicilan_mobil", "Cicilan Mobil"]];
const OFFICE_DEFAULTS = ["Bensin", "Tol", "Iuran Sampah & Keamanan", "Galon", "Lembur", "Parkir", "ATK", "Uang Tips"].map((name) => ({ name, amount: "" }));

const Stat = ({ icon: Icon, label, value, accent, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay }}
    className="rounded-2xl border border-brand-line bg-white p-5 shadow-[0_8px_30px_rgba(45,35,30,0.05)]"
    data-testid={`report-stat-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div className="flex items-start justify-between">
      <p className="text-sm font-medium text-brand-muted">{label}</p>
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent || "bg-brand-gold/15 text-brand-gold"}`}>
        <Icon className="h-[18px] w-[18px]" />
      </div>
    </div>
    <p className="mt-3 font-serif text-2xl font-bold text-brand-espresso">{idrShort(value)}</p>
  </motion.div>
);

export default function Reports() {
  const [data, setData] = useState(null);
  const [prod, setProd] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [tab, setTab] = useState("ringkasan");
  const [dombaSort, setDombaSort] = useState("asc");
  const [masakSort, setMasakSort] = useState("asc");
  const [labaSort, setLabaSort] = useState("asc");
  const [costMonth, setCostMonth] = useState(new Date().toISOString().slice(0, 7));
  const [costValues, setCostValues] = useState({});
  const [office, setOffice] = useState(OFFICE_DEFAULTS);
  const [pettyOffice, setPettyOffice] = useState([]);
  const [pettyTotal, setPettyTotal] = useState(0);
  const [costSaving, setCostSaving] = useState(false);
  const [labaMonth, setLabaMonth] = useState(new Date().toISOString().slice(0, 7));
  const [labaCosts, setLabaCosts] = useState({ values: {}, office: [] });
  const [ringkasanMonth, setRingkasanMonth] = useState(new Date().toISOString().slice(0, 7));
  const [ringkasanCosts, setRingkasanCosts] = useState({ values: {}, office: [] });

  const load = useCallback(() => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    api.get("/reports/financial", { params }).then(({ data }) => setData(data)).catch(() => {});
    api.get("/reports/production", { params }).then(({ data }) => setProd(data)).catch(() => {});
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/reports/monthly-costs", { params: { month: costMonth } })
      .then(({ data }) => {
        setCostValues(data.values || {});
        setOffice(data.office && data.office.length ? data.office : OFFICE_DEFAULTS);
        setPettyOffice(data.petty_office || []);
        setPettyTotal(data.petty_total || 0);
      }).catch(() => {});
  }, [costMonth]);

  useEffect(() => {
    api.get("/reports/monthly-costs", { params: { month: labaMonth } })
      .then(({ data }) => setLabaCosts({ values: data.values || {}, office: data.office || [], petty_total: data.petty_total || 0 }))
      .catch(() => {});
  }, [labaMonth]);

  useEffect(() => {
    api.get("/reports/monthly-costs", { params: { month: ringkasanMonth } })
      .then(({ data }) => setRingkasanCosts({ values: data.values || {}, office: data.office || [], petty_total: data.petty_total || 0 }))
      .catch(() => {});
  }, [ringkasanMonth]);

  const updateOffice = (i, field, val) => setOffice((o) => o.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const addOffice = () => setOffice((o) => [...o, { name: "", amount: "" }]);
  const removeOffice = (i) => setOffice((o) => o.filter((_, idx) => idx !== i));

  const saveCosts = async () => {
    setCostSaving(true);
    try {
      await api.put("/reports/monthly-costs", { month: costMonth, values: costValues, office });
    } catch (e) { /* noop */ } finally { setCostSaving(false); }
  };

  const doExport = async () => {
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get("/reports/financial/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-keuangan-${start || "awal"}_${end || "akhir"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };

  const doExportProd = async (kind) => {
    try {
      const params = { kind };
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get("/reports/production/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `pembayaran-${kind}-${start || "awal"}_${end || "akhir"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };

  const doExportCost = async () => {
    try {
      const res = await api.get("/reports/monthly-costs/export", { params: { month: costMonth }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `biaya-bulanan-${costMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };

  const setBiayaKirim = (orderId, val) => setProd((p) => ({ ...p, profit_payments: (p.profit_payments || []).map((r) => r.order_id === orderId ? { ...r, shipping: val } : r) }));
  const saveBiayaKirim = async (orderId, val) => {
    try {
      await api.patch(`/orders/${orderId}/status`, { delivery_cost: Number(val) || 0 });
      toast.success("Biaya Kirim tersimpan");
    } catch (e) {
      toast.error("Gagal menyimpan Biaya Kirim");
    }
  };

  const s = data?.summary;
  const chart = (data?.monthly || []).map((m) => ({ ...m, label: monthLabel(m.month) }));
  const dombaRows = [...((prod?.lamb_payments) || [])].sort((a, b) => dombaSort === "asc" ? String(a.slaughter_date).localeCompare(String(b.slaughter_date)) : String(b.slaughter_date).localeCompare(String(a.slaughter_date)));
  const masakRows = [...((prod?.cook_payments) || [])].sort((a, b) => masakSort === "asc" ? String(a.event_date).localeCompare(String(b.event_date)) : String(b.event_date).localeCompare(String(a.event_date)));
  const dombaTotal = dombaRows.reduce((sum, r) => sum + (r.total || 0), 0);
  const masakTotal = masakRows.reduce((sum, r) => sum + (r.total || 0), 0);
  const labaRows = [...((prod?.profit_payments) || [])].sort((a, b) => labaSort === "asc" ? String(a.event_date).localeCompare(String(b.event_date)) : String(b.event_date).localeCompare(String(a.event_date)));
  const labaVal = (r) => (r.revenue || 0) - ((r.lamb_base || 0) + (r.lamb_cook || 0) + (r.box_cook || 0) + (r.addon1_cost || 0) + (r.addon2_cost || 0) + (r.addon3_cost || 0) + (Number(r.shipping) || 0));
  const labaTotal = labaRows.reduce((sum, r) => sum + labaVal(r), 0);
  const labaVarTotal = VAR_COSTS.reduce((sum, [k]) => sum + (Number(labaCosts.values?.[k]) || 0), 0);
  const labaFixTotal = FIX_COSTS.reduce((sum, [k]) => sum + (Number(labaCosts.values?.[k]) || 0), 0);
  const labaOfficeTotal = (labaCosts.office || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0) + (Number(labaCosts.petty_total) || 0);
  const labaNet = labaTotal - labaVarTotal - labaFixTotal - labaOfficeTotal;
  const labaShippingTotal = labaRows.reduce((sum, r) => sum + (Number(r.shipping) || 0), 0);
  const ringkasanCostTotal =
    VAR_COSTS.reduce((sum, [k]) => sum + (Number(ringkasanCosts.values?.[k]) || 0), 0) +
    FIX_COSTS.reduce((sum, [k]) => sum + (Number(ringkasanCosts.values?.[k]) || 0), 0) +
    (ringkasanCosts.office || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0) +
    (Number(ringkasanCosts.petty_total) || 0);
  const ringkasanAds = Number(ringkasanCosts.values?.biaya_iklan) || 0;
  const ringkasanAdsPerLamb = (s?.lamb_qty || 0) > 0 ? ringkasanAds / s.lamb_qty : 0;
  const totalVar = VAR_COSTS.reduce((sum, [k]) => sum + (Number(costValues[k]) || 0), 0);
  const totalFix = FIX_COSTS.reduce((sum, [k]) => sum + (Number(costValues[k]) || 0), 0);
  const totalOffice = office.reduce((sum, r) => sum + (Number(r.amount) || 0), 0) + (Number(pettyTotal) || 0);
  const totalCost = totalVar + totalFix + totalOffice;

  return (
    <div className="p-6 md:p-10" data-testid="reports-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4 animate-fade-up">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-gold">Keuangan</p>
          <h1 className="mt-1 font-serif text-4xl font-bold text-brand-espresso">Laporan Keuangan</h1>
        </div>
      </div>

      {/* Date filter */}
      <div className="mb-6 flex flex-wrap items-center gap-3 no-print">
        <div className="flex items-center gap-2 rounded-md border border-brand-line bg-white px-3 py-1.5">
          <CalendarDays className="h-4 w-4 text-brand-muted" />
          <span className="text-xs text-brand-muted">Periode:</span>
          <DateField value={start} onChange={setStart} hideIcon testid="report-start" className="h-8 w-40 border-0 bg-transparent px-0 shadow-none" placeholder="Dari tgl" />
          <span className="text-brand-muted">–</span>
          <DateField value={end} onChange={setEnd} hideIcon testid="report-end" className="h-8 w-40 border-0 bg-transparent px-0 shadow-none" placeholder="Sampai tgl" />
          {(start || end) && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setStart(""); setEnd(""); }} data-testid="report-clear">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <span className="text-sm text-brand-muted">{s ? `${s.order_count} pesanan` : ""}</span>
      </div>

      {/* Sub-menu tabs */}
      <div className="mb-6 flex flex-wrap gap-2 no-print" data-testid="report-tabs">
        {[["ringkasan", "Ringkasan"], ["domba", "Pembayaran Domba"], ["masak", "Pembayaran Masak & Add On"], ["biaya", "Biaya Bulanan"], ["laba", "Laba Rugi"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`report-tab-${k}`}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${tab === k ? "bg-brand-espresso text-white" : "border border-brand-line bg-white text-brand-espresso hover:border-brand-gold"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "ringkasan" && (<>
      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <Stat icon={Receipt} label="Omzet" value={s?.omzet} delay={0} />
        <Stat icon={Wallet} label="Pembayaran Domba" value={dombaTotal} accent="bg-green-100 text-green-700" delay={0.05} />
        <Stat icon={HandCoins} label="Pembayaran Masak & Add On" value={masakTotal} accent="bg-amber-100 text-amber-700" delay={0.1} />
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.15 }} className="rounded-2xl border border-brand-line bg-white p-5 shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="report-stat-biaya-bulanan">
          <div className="flex items-start justify-between">
            <p className="text-sm font-medium text-brand-muted">Biaya Bulanan</p>
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-100 text-rose-700"><Coins className="h-[18px] w-[18px]" /></div>
          </div>
          <p className="mt-3 font-serif text-2xl font-bold text-brand-espresso">{idrShort(ringkasanCostTotal)}</p>
          <input type="month" value={ringkasanMonth} onChange={(e) => setRingkasanMonth(e.target.value)} data-testid="ringkasan-office-month" className="mt-2 w-full rounded-md border border-brand-line px-2 py-1 text-xs text-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-gold" />
        </motion.div>
        <Stat icon={TrendingUp} label="Laba Bersih" value={labaTotal - ringkasanCostTotal} accent="bg-brand-espresso/10 text-brand-espresso" delay={0.2} />
      </div>

      {/* Secondary */}
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-brand-line bg-white p-4"><p className="text-xs text-brand-muted">Jumlah Konsumen</p><p className="mt-1 font-serif text-lg font-bold text-brand-espresso">{new Intl.NumberFormat("id-ID").format(s?.order_count || 0)}</p></div>
        <div className="rounded-xl border border-brand-line bg-white p-4"><p className="text-xs text-brand-muted">Jumlah Ekor Domba</p><p className="mt-1 font-serif text-lg font-bold text-brand-espresso">{new Intl.NumberFormat("id-ID").format(s?.lamb_qty || 0)} ekor</p></div>
        <div className="rounded-xl border border-brand-line bg-white p-4"><p className="text-xs text-brand-muted">Biaya Iklan</p><p className="mt-1 font-serif text-lg font-bold text-brand-espresso">{idrShort(ringkasanAds)}</p></div>
        <div className="rounded-xl border border-brand-line bg-white p-4"><p className="text-xs text-brand-muted">Biaya Iklan/Jumlah Ekor</p><p className="mt-1 font-serif text-lg font-bold text-brand-espresso">{idrShort(ringkasanAdsPerLamb)}</p></div>
        <div className="rounded-xl border border-brand-line bg-white p-4" data-testid="stat-persentase-laba"><p className="text-xs text-brand-muted">Persentase Laba Bersih/Omzet</p><p className="mt-1 font-serif text-lg font-bold text-brand-espresso">{(s?.omzet ? ((labaTotal - ringkasanCostTotal) / s.omzet) * 100 : 0).toLocaleString("id-ID", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%</p></div>
        <div className="rounded-xl border border-brand-line bg-white p-4" data-testid="stat-roas"><p className="text-xs text-brand-muted">ROAS</p><p className="mt-1 font-serif text-lg font-bold text-brand-espresso">{(ringkasanAds ? (labaTotal - ringkasanCostTotal + ringkasanAds) / ringkasanAds : 0).toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x</p></div>
      </div>

      {/* Chart */}
      <div className="mt-8 rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)]">
        <h2 className="mb-4 font-serif text-xl font-bold text-brand-espresso">Pendapatan per Bulan</h2>
        {chart.length === 0 ? (
          <p className="py-10 text-center text-brand-muted">Belum ada data pada periode ini.</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#Eee6d8" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#8a7a6a" }} />
              <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(0)}jt`} tick={{ fontSize: 12, fill: "#8a7a6a" }} />
              <Tooltip formatter={(v) => idrShort(v)} />
              <Legend />
              <Bar dataKey="omzet" name="Omzet" fill="#C9A24B" radius={[4, 4, 0, 0]} />
              <Bar dataKey="net_profit" name="Laba Bersih" fill="#4a3728" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Cash flow chart */}
      <div className="mt-8 rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)]">
        <h2 className="mb-1 font-serif text-xl font-bold text-brand-espresso">Arus Kas</h2>
        <p className="mb-4 text-sm text-brand-muted">Uang yang sudah diterima vs sisa tagihan (piutang) per bulan.</p>
        {chart.length === 0 ? (
          <p className="py-10 text-center text-brand-muted">Belum ada data pada periode ini.</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#Eee6d8" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#8a7a6a" }} />
              <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(0)}jt`} tick={{ fontSize: 12, fill: "#8a7a6a" }} />
              <Tooltip formatter={(v) => idrShort(v)} />
              <Legend />
              <Bar dataKey="received" name="Sudah Diterima" fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar dataKey="outstanding" name="Sisa Tagihan" fill="#d97706" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      </>)}

      {tab === "domba" && (
        <div>
          <div className="mb-4 flex justify-end no-print">
            <Button onClick={() => doExportProd("domba")} data-testid="export-domba-btn" className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
              <Download className="h-4 w-4" /> Ekspor Excel
            </Button>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-brand-line bg-white shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="report-domba">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-line bg-brand-surface/60 text-left text-xs uppercase tracking-wide text-brand-muted">
                <th className="px-4 py-3 font-semibold">
                  <button onClick={() => setDombaSort(dombaSort === "asc" ? "desc" : "asc")} data-testid="domba-sort-date" className="inline-flex items-center gap-1 uppercase hover:text-brand-gold">
                    Tanggal Sembelih <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold">Nama Anak</th>
                <th className="px-4 py-3 font-semibold">Tipe Domba</th>
                <th className="px-4 py-3 text-center font-semibold">Jumlah Ekor</th>
                <th className="px-4 py-3 text-right font-semibold">Harga Satuan</th>
                <th className="px-4 py-3 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {dombaRows.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-brand-muted">Belum ada data.</td></tr>
              ) : dombaRows.map((r, i) => (
                <tr key={i} className="border-b border-brand-line/60 hover:bg-brand-surface/40" data-testid={`domba-row-${i}`}>
                  <td className="px-4 py-3 font-medium text-brand-espresso">{formatDate(r.slaughter_date)}</td>
                  <td className="px-4 py-3">{r.child_name}</td>
                  <td className="px-4 py-3">{r.lamb_type}</td>
                  <td className="px-4 py-3 text-center">{r.lamb_count}</td>
                  <td className="px-4 py-3 text-right">{formatIDR(r.unit_price)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-brand-espresso">{formatIDR(r.total)}</td>
                </tr>
              ))}
            </tbody>
            {dombaRows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-brand-line bg-brand-surface/40 font-bold text-brand-espresso">
                  <td className="px-4 py-3" colSpan={5}>TOTAL</td>
                  <td className="px-4 py-3 text-right text-brand-gold">{formatIDR(dombaTotal)}</td>
                </tr>
              </tfoot>
            )}
          </table>
          </div>
        </div>
      )}

      {tab === "masak" && (
        <div>
          <div className="mb-4 flex justify-end no-print">
            <Button onClick={() => doExportProd("masak")} data-testid="export-masak-btn" className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
              <Download className="h-4 w-4" /> Ekspor Excel
            </Button>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-brand-line bg-white shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="report-masak">
          <table className="w-full whitespace-nowrap text-sm">
            <thead>
              <tr className="border-b border-brand-line bg-brand-surface/60 text-left text-xs uppercase tracking-wide text-brand-muted">
                <th className="px-3 py-3 font-semibold">
                  <button onClick={() => setMasakSort(masakSort === "asc" ? "desc" : "asc")} data-testid="masak-sort-date" className="inline-flex items-center gap-1 uppercase hover:text-brand-gold">
                    Tanggal Aqiqah <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </th>
                <th className="px-3 py-3 font-semibold">Nama Anak</th>
                <th className="px-3 py-3 font-semibold">Tipe Domba</th>
                <th className="px-3 py-3 text-center font-semibold">Jml Ekor</th>
                <th className="px-3 py-3 text-right font-semibold">Harga Masak/Ekor</th>
                <th className="px-3 py-3 text-right font-semibold">Biaya Masak Domba</th>
                <th className="px-3 py-3 font-semibold">Paket Nasi Box</th>
                <th className="px-3 py-3 text-center font-semibold">Jml Box</th>
                <th className="px-3 py-3 text-right font-semibold">Harga Satuan Box</th>
                <th className="px-3 py-3 text-right font-semibold">Biaya Masak Box</th>
                <th className="px-3 py-3 font-semibold">Add On 1</th>
                <th className="px-3 py-3 text-center font-semibold">QTY 1</th>
                <th className="px-3 py-3 text-right font-semibold">Hrg Satuan 1</th>
                <th className="px-3 py-3 text-right font-semibold">Biaya 1</th>
                <th className="px-3 py-3 font-semibold">Add On 2</th>
                <th className="px-3 py-3 text-center font-semibold">QTY 2</th>
                <th className="px-3 py-3 text-right font-semibold">Hrg Satuan 2</th>
                <th className="px-3 py-3 text-right font-semibold">Biaya 2</th>
                <th className="px-3 py-3 font-semibold">Add On 3</th>
                <th className="px-3 py-3 text-center font-semibold">QTY 3</th>
                <th className="px-3 py-3 text-right font-semibold">Hrg Satuan 3</th>
                <th className="px-3 py-3 text-right font-semibold">Biaya 3</th>
                <th className="px-3 py-3 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {masakRows.length === 0 ? (
                <tr><td colSpan={23} className="py-12 text-center text-brand-muted">Belum ada data.</td></tr>
              ) : masakRows.map((r, i) => (
                <tr key={i} className="border-b border-brand-line/60 hover:bg-brand-surface/40" data-testid={`masak-row-${i}`}>
                  <td className="px-3 py-3 font-medium text-brand-espresso">{formatDate(r.event_date)}</td>
                  <td className="px-3 py-3">{r.child_name}</td>
                  <td className="px-3 py-3">{r.lamb_type}</td>
                  <td className="px-3 py-3 text-center">{r.lamb_count}</td>
                  <td className="px-3 py-3 text-right">{formatIDR(r.lamb_cook_unit)}</td>
                  <td className="px-3 py-3 text-right">{formatIDR(r.lamb_cook_total)}</td>
                  <td className="px-3 py-3">{r.box_name}</td>
                  <td className="px-3 py-3 text-center">{r.box_count}</td>
                  <td className="px-3 py-3 text-right">{formatIDR(r.box_unit)}</td>
                  <td className="px-3 py-3 text-right">{formatIDR(r.box_cook_total)}</td>
                  <td className="px-3 py-3">{r.addon1_name || "-"}</td>
                  <td className="px-3 py-3 text-center">{r.addon1_qty || ""}</td>
                  <td className="px-3 py-3 text-right">{r.addon1_name ? formatIDR(r.addon1_unit || 0) : "-"}</td>
                  <td className="px-3 py-3 text-right">{r.addon1_name ? formatIDR(r.addon1_cost || 0) : "-"}</td>
                  <td className="px-3 py-3">{r.addon2_name || "-"}</td>
                  <td className="px-3 py-3 text-center">{r.addon2_qty || ""}</td>
                  <td className="px-3 py-3 text-right">{r.addon2_name ? formatIDR(r.addon2_unit || 0) : "-"}</td>
                  <td className="px-3 py-3 text-right">{r.addon2_name ? formatIDR(r.addon2_cost || 0) : "-"}</td>
                  <td className="px-3 py-3">{r.addon3_name || "-"}</td>
                  <td className="px-3 py-3 text-center">{r.addon3_qty || ""}</td>
                  <td className="px-3 py-3 text-right">{r.addon3_name ? formatIDR(r.addon3_unit || 0) : "-"}</td>
                  <td className="px-3 py-3 text-right">{r.addon3_name ? formatIDR(r.addon3_cost || 0) : "-"}</td>
                  <td className="px-3 py-3 text-right font-semibold text-brand-espresso">{formatIDR(r.total)}</td>
                </tr>
              ))}
            </tbody>
            {masakRows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-brand-line bg-brand-surface/40 font-bold text-brand-espresso">
                  <td className="px-3 py-3" colSpan={22}>TOTAL BIAYA MASAK</td>
                  <td className="px-3 py-3 text-right text-brand-gold">{formatIDR(masakTotal)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        </div>
      )}

      {tab === "laba" && (
        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 no-print">
            <div className="flex items-center gap-2 rounded-md border border-brand-line bg-white px-3 py-1.5">
              <CalendarDays className="h-4 w-4 text-brand-muted" />
              <span className="text-sm text-brand-muted">Bulan Biaya:</span>
              <input type="month" value={labaMonth} onChange={(e) => setLabaMonth(e.target.value)} data-testid="laba-month" className="border-0 p-0 text-sm text-brand-espresso focus:outline-none" />
            </div>
            <Button onClick={() => doExportProd("laba")} data-testid="export-laba-btn" className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
              <Download className="h-4 w-4" /> Ekspor Excel
            </Button>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-brand-line bg-white shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="report-laba">
            <table className="w-full whitespace-nowrap text-sm">
              <thead>
                <tr className="border-b border-brand-line bg-brand-surface/60 text-left text-xs uppercase tracking-wide text-brand-muted">
                  <th className="px-3 py-3 font-semibold">
                    <button onClick={() => setLabaSort(labaSort === "asc" ? "desc" : "asc")} data-testid="laba-sort-date" className="inline-flex items-center gap-1 uppercase hover:text-brand-gold">
                      Tanggal Aqiqah <ArrowUpDown className="h-3.5 w-3.5" />
                    </button>
                  </th>
                  <th className="px-3 py-3 font-semibold">Nama Anak</th>
                  <th className="px-3 py-3 text-right font-semibold">Omzet</th>
                  <th className="px-3 py-3 text-right font-semibold">Biaya Domba</th>
                  <th className="px-3 py-3 text-right font-semibold">Masak Domba</th>
                  <th className="px-3 py-3 text-right font-semibold">Masak Nasi Box</th>
                  <th className="px-3 py-3 text-right font-semibold">Biaya Add On 1</th>
                  <th className="px-3 py-3 text-right font-semibold">Biaya Add On 2</th>
                  <th className="px-3 py-3 text-right font-semibold">Biaya Add On 3</th>
                  <th className="px-3 py-3 text-right font-semibold">Biaya Kirim</th>
                  <th className="px-3 py-3 text-right font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {labaRows.length === 0 ? (
                  <tr><td colSpan={11} className="py-12 text-center text-brand-muted">Belum ada data.</td></tr>
                ) : labaRows.map((r, i) => (
                  <tr key={r.order_id || i} className="border-b border-brand-line/60 hover:bg-brand-surface/40" data-testid={`laba-row-${i}`}>
                    <td className="px-3 py-3 font-medium text-brand-espresso">{formatDate(r.event_date)}</td>
                    <td className="px-3 py-3">{r.child_name}</td>
                    <td className="px-3 py-3 text-right">{formatIDR(r.revenue)}</td>
                    <td className="px-3 py-3 text-right">{formatIDR(r.lamb_base)}</td>
                    <td className="px-3 py-3 text-right">{formatIDR(r.lamb_cook)}</td>
                    <td className="px-3 py-3 text-right">{formatIDR(r.box_cook)}</td>
                    <td className="px-3 py-3 text-right">{formatIDR(r.addon1_cost)}</td>
                    <td className="px-3 py-3 text-right">{formatIDR(r.addon2_cost)}</td>
                    <td className="px-3 py-3 text-right">{formatIDR(r.addon3_cost)}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <input type="number" value={r.shipping ?? 0}
                          onChange={(e) => setBiayaKirim(r.order_id, e.target.value)}
                          data-testid={`laba-kirim-${i}`}
                          className="w-24 rounded-md border border-brand-line bg-white px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold" />
                        <button type="button" onClick={() => saveBiayaKirim(r.order_id, r.shipping)}
                          data-testid={`laba-kirim-save-${i}`} title="Simpan Biaya Kirim"
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
                          <Save className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className={`px-3 py-3 text-right font-bold ${labaVal(r) < 0 ? "text-destructive" : "text-green-700"}`}>{formatIDR(labaVal(r))}</td>
                  </tr>
                ))}
              </tbody>
              {labaRows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-brand-line bg-brand-surface/40 font-bold text-brand-espresso">
                    <td className="px-3 py-3" colSpan={10}>TOTAL LABA KOTOR</td>
                    <td className={`px-3 py-3 text-right ${labaTotal < 0 ? "text-destructive" : "text-green-700"}`}>{formatIDR(labaTotal)}</td>
                  </tr>
                  <tr className="border-b border-brand-line/60 text-brand-espresso">
                    <td className="px-3 py-2 text-right" colSpan={10}>Variabel Cost ({monthLabel(labaMonth)})</td>
                    <td className="px-3 py-2 text-right text-destructive">-{formatIDR(labaVarTotal)}</td>
                  </tr>
                  <tr className="border-b border-brand-line/60 text-brand-espresso">
                    <td className="px-3 py-2 text-right" colSpan={10}>Fix Cost ({monthLabel(labaMonth)})</td>
                    <td className="px-3 py-2 text-right text-destructive">-{formatIDR(labaFixTotal)}</td>
                  </tr>
                  <tr className="border-b border-brand-line/60 text-brand-espresso">
                    <td className="px-3 py-2 text-right" colSpan={10}>Pengeluaran Kantor ({monthLabel(labaMonth)})</td>
                    <td className="px-3 py-2 text-right text-destructive">-{formatIDR(labaOfficeTotal)}</td>
                  </tr>
                  <tr className="border-t-2 border-brand-line bg-brand-espresso font-bold text-white">
                    <td className="px-3 py-3" colSpan={10}>LABA BERSIH ({monthLabel(labaMonth)})</td>
                    <td className={`px-3 py-3 text-right ${labaNet < 0 ? "text-red-300" : "text-brand-gold"}`}>{formatIDR(labaNet)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {tab === "biaya" && (
        <div>
          <div className="mb-4 flex flex-wrap items-center gap-3 no-print">
            <div className="flex items-center gap-2 rounded-md border border-brand-line bg-white px-3 py-1.5">
              <CalendarDays className="h-4 w-4 text-brand-muted" />
              <span className="text-sm text-brand-muted">Bulan:</span>
              <input type="month" value={costMonth} onChange={(e) => setCostMonth(e.target.value)} data-testid="cost-month" className="border-0 p-0 text-sm text-brand-espresso focus:outline-none" />
            </div>
            <Button onClick={saveCosts} disabled={costSaving} data-testid="cost-save-btn" className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
              {costSaving ? "Menyimpan..." : "Simpan"}
            </Button>
            <Button onClick={doExportCost} variant="outline" data-testid="export-cost-btn" className="gap-2 border-brand-line text-brand-espresso hover:bg-brand-surface">
              <Download className="h-4 w-4" /> Ekspor Excel
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="var-cost-card">
              <h2 className="mb-4 font-serif text-xl font-bold text-brand-espresso">Variabel Cost</h2>
              <div className="space-y-3">
                {VAR_COSTS.map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-brand-espresso">{label}</span>
                    <div className="flex items-center gap-1 rounded-md border border-brand-line px-2 py-1">
                      <span className="text-xs text-brand-muted">Rp</span>
                      <input type="number" value={costValues[k] ?? ""} placeholder="0"
                        onChange={(e) => setCostValues((v) => ({ ...v, [k]: e.target.value }))}
                        data-testid={`cost-${k}`} className="w-32 border-0 p-0 text-right text-sm focus:outline-none" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between border-t-2 border-brand-line pt-3 font-bold text-brand-espresso">
                <span>Total Variabel Cost</span>
                <span className="text-brand-gold">{formatIDR(totalVar)}</span>
              </div>
            </div>

            <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="fix-cost-card">
              <h2 className="mb-4 font-serif text-xl font-bold text-brand-espresso">Fix Cost</h2>
              <div className="space-y-3">
                {FIX_COSTS.map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-brand-espresso">{label}</span>
                    <div className="flex items-center gap-1 rounded-md border border-brand-line px-2 py-1">
                      <span className="text-xs text-brand-muted">Rp</span>
                      <input type="number" value={costValues[k] ?? ""} placeholder="0"
                        onChange={(e) => setCostValues((v) => ({ ...v, [k]: e.target.value }))}
                        data-testid={`cost-${k}`} className="w-32 border-0 p-0 text-right text-sm focus:outline-none" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between border-t-2 border-brand-line pt-3 font-bold text-brand-espresso">
                <span>Total Fix Cost</span>
                <span className="text-brand-gold">{formatIDR(totalFix)}</span>
              </div>
            </div>

            <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="office-cost-card">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-serif text-xl font-bold text-brand-espresso">Pengeluaran Kantor</h2>
                <Button type="button" variant="outline" onClick={addOffice} data-testid="office-add-btn" className="h-8 gap-1 border-brand-line px-2 text-xs">
                  <Plus className="h-3.5 w-3.5" /> Tambah
                </Button>
              </div>
              <div className="space-y-2">
                {office.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={r.name} placeholder="Nama item"
                      onChange={(e) => updateOffice(i, "name", e.target.value)}
                      data-testid={`office-name-${i}`} className="flex-1 rounded-md border border-brand-line px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold" />
                    <div className="flex items-center gap-1 rounded-md border border-brand-line px-2 py-1">
                      <span className="text-xs text-brand-muted">Rp</span>
                      <input type="number" value={r.amount ?? ""} placeholder="0"
                        onChange={(e) => updateOffice(i, "amount", e.target.value)}
                        data-testid={`office-amount-${i}`} className="w-24 border-0 p-0 text-right text-sm focus:outline-none" />
                    </div>
                    <button type="button" onClick={() => removeOffice(i)} data-testid={`office-del-${i}`} className="text-brand-muted hover:text-destructive">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {pettyOffice.map((r, i) => (
                  <div key={`petty-${i}`} className="flex items-center gap-2" data-testid={`office-petty-${i}`}>
                    <div className="flex flex-1 items-center gap-1 rounded-md border border-dashed border-brand-line bg-brand-surface/40 px-2 py-1 text-sm text-brand-muted">
                      <Wallet className="h-3.5 w-3.5 text-brand-gold" /> {r.name}
                    </div>
                    <div className="flex items-center gap-1 rounded-md border border-dashed border-brand-line bg-brand-surface/40 px-2 py-1">
                      <span className="text-xs text-brand-muted">Rp</span>
                      <span className="w-24 text-right text-sm text-brand-muted">{new Intl.NumberFormat("id-ID").format(Math.round(Number(r.amount) || 0))}</span>
                    </div>
                    <span className="w-4" />
                  </div>
                ))}
              </div>
              {pettyOffice.length > 0 && (
                <p className="mt-2 text-xs text-brand-muted">Item putus-putus otomatis dari Buku Kas Harian (Petty Cash) cabang.</p>
              )}
              <div className="mt-4 flex items-center justify-between border-t-2 border-brand-line pt-3 font-bold text-brand-espresso">
                <span>Total Pengeluaran Kantor</span>
                <span className="text-brand-gold">{formatIDR(totalOffice)}</span>
              </div>
            </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between rounded-2xl border border-brand-line bg-brand-espresso p-6 text-white shadow-[0_8px_30px_rgba(45,35,30,0.05)]" data-testid="total-cost-card">
            <span className="font-serif text-lg font-bold">Total Biaya Bulanan ({monthLabel(costMonth)})</span>
            <span className="font-serif text-2xl font-bold text-brand-gold">{formatIDR(totalCost)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
